import "./workspace-help.css";
import { findHelpGuides, type HelpContent, type HelpContext, type HelpGuide } from "./types";

const helpEvent = "academy:open-help";
/** Existing React controls and the shared drawer communicate without sharing React runtimes. */
export function openWorkspaceHelp(guideId?: string) {
  window.dispatchEvent(new CustomEvent(helpEvent, { detail: guideId }));
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, text?: string, className?: string) {
  const node = document.createElement(tag);
  if (text) node.textContent = text;
  if (className) node.className = className;
  return node;
}
function button(label: string, action: () => void, className = "") {
  const node = element("button", label, className);
  node.type = "button";
  node.addEventListener("click", action);
  return node;
}

/** A native modal dialog supplies focus containment and makes the workspace inert while reading. */
export function mountWorkspaceHelp(options: {
  content: HelpContent;
  context: HelpContext;
  navigate: (area: string, page: string) => void;
}) {
  const { content, context, navigate } = options;
  const dialog = element("dialog", undefined, "academy-help");
  dialog.setAttribute("aria-labelledby", "academy-help-title");
  const shell = element("div", undefined, "academy-help-shell");
  const header = element("header", undefined, "academy-help-header");
  const titleGroup = element("div");
  titleGroup.append(element("p", content.title, "academy-help-eyebrow"));
  const title = element("h2", "Help & guidance");
  title.id = "academy-help-title";
  titleGroup.append(title);
  let returnFocus: HTMLElement | null = null;
  let previousOverflow = "";
  const close = () => {
    if (!dialog.open) return;
    dialog.close();
    document.documentElement.style.overflow = previousOverflow;
    (returnFocus?.isConnected ? returnFocus : launcher).focus();
  };
  const closeButton = button("Close help", close, "academy-help-close");
  header.append(titleGroup, closeButton);
  const body = element("div", undefined, "academy-help-body");
  const footer = element("footer", undefined, "academy-help-footer");
  footer.append(
    element("span", "Need another person’s help?"),
    button("Prepare support summary", () => renderSupport()),
  );
  shell.append(header, body, footer);
  dialog.append(shell);
  const launcher = button("?  Help", () => open(), "academy-help-launcher");
  launcher.setAttribute("aria-label", "Open help and guidance");
  launcher.setAttribute("aria-haspopup", "dialog");
  document.body.append(launcher, dialog);
  let query = "";
  let category = "";
  let supportDraft = "";
  const checked = new Set<string>();

  function resetBody() {
    body.replaceChildren();
    body.scrollTop = 0;
    footer.hidden = false;
  }
  function focusHeading(heading: HTMLElement) {
    heading.tabIndex = -1;
    heading.focus();
  }
  function renderLibrary(focus = false) {
    resetBody();
    const contextBox = element("section", undefined, "academy-help-context");
    contextBox.append(
      element("p", "You are here", "academy-help-eyebrow"),
      element("h3", context.title),
      element("p", content.intro),
    );
    body.append(contextBox);
    const searchLabel = element("label", "Search guides", "academy-help-label");
    const search = element("input");
    search.type = "search";
    search.placeholder = "Try joining a class, permissions, evidence…";
    search.value = query;
    searchLabel.append(search);
    const categoryLabel = element("label", "Topic", "academy-help-label");
    const select = element("select");
    for (const item of ["", ...new Set(content.guides.map(guide => guide.category))]) {
      const option = element("option", item || "All topics");
      option.value = item;
      select.append(option);
    }
    select.value = category;
    categoryLabel.append(select);
    const filters = element("div", undefined, "academy-help-filters");
    filters.append(searchLabel, categoryLabel);
    const count = element("p", undefined, "academy-help-count");
    count.setAttribute("role", "status");
    const results = element("div", undefined, "academy-help-results");
    const update = () => {
      const guides = findHelpGuides(content, context, query, category);
      count.textContent = `${guides.length} ${guides.length === 1 ? "guide" : "guides"}${query || category ? " found" : " · Related to your page first"}`;
      results.replaceChildren();
      if (!guides.length) {
        const empty = element("section", undefined, "academy-help-context");
        empty.append(
          element("h3", "No matching guides"),
          element(
            "p",
            "Try a shorter phrase, choose another topic, or prepare a support summary with what you tried.",
          ),
          button("Clear search and filters", () => {
            query = "";
            category = "";
            renderLibrary(true);
          }),
        );
        results.append(empty);
      }
      for (const guide of guides) {
        const card = button("", () => renderGuide(guide), "academy-help-card");
        const related = guide.pages?.includes(context.page) || guide.areas.includes(context.area);
        card.append(
          element("span", `${guide.category}${related ? " · For this page" : ""}`, "academy-help-eyebrow"),
          element("strong", guide.title),
          element("span", guide.summary),
          element("small", `${guide.steps.length} steps · Read guide →`),
        );
        results.append(card);
      }
    };
    search.addEventListener("input", () => {
      query = search.value;
      update();
    });
    select.addEventListener("change", () => {
      category = select.value;
      update();
    });
    body.append(filters, count, results);
    update();
    if (focus) search.focus();
  }

  function renderGuide(guide: HelpGuide) {
    resetBody();
    body.append(button("← All guides", () => renderLibrary(true), "academy-help-back"));
    const heading = element("h3", guide.title, "academy-help-article-title");
    body.append(element("p", guide.category, "academy-help-eyebrow"), heading, element("p", guide.summary));
    const before = element("section", undefined, "academy-help-context");
    before.append(element("h4", "Before you start"), element("p", guide.before));
    body.append(before, element("h4", "Follow these steps"));
    const list = element("ol", undefined, "academy-help-steps");
    guide.steps.forEach((step, index) => {
      const row = element("li");
      const label = element("label");
      const check = element("input");
      check.type = "checkbox";
      const key = `${guide.id}:${index}`;
      check.checked = checked.has(key);
      check.addEventListener("change", () => {
        if (check.checked) checked.add(key);
        else checked.delete(key);
      });
      label.append(check, element("span", step));
      row.append(label);
      list.append(row);
    });
    body.append(
      list,
      element(
        "p",
        "Use the checkboxes as a reading checklist. They do not perform or verify actions in your workspace.",
        "academy-help-note",
      ),
    );
    const outcome = element("section", undefined, "academy-help-outcome");
    outcome.append(element("h4", "What to check afterwards"), element("p", guide.outcome));
    body.append(outcome, element("h4", "If something goes wrong"));
    for (const problem of guide.problems) {
      const details = element("details");
      details.append(element("summary", problem.question), element("p", problem.answer));
      body.append(details);
    }
    if (guide.actions.length) {
      const actions = element("nav", undefined, "academy-help-actions");
      actions.setAttribute("aria-label", "Guide destinations");
      for (const action of guide.actions) {
        actions.append(
          button(action.label, () => {
            close();
            navigate(action.area, action.page);
          }),
        );
      }
      body.append(
        element("h4", "Go to the workspace"),
        element(
          "p",
          "Opening a page closes help. Save any unfinished form before leaving it.",
          "academy-help-note",
        ),
        actions,
      );
    }
    focusHeading(heading);
  }

  function renderSupport() {
    resetBody();
    footer.hidden = true;
    body.append(button("← All guides", () => renderLibrary(true), "academy-help-back"));
    const heading = element("h3", "Prepare a support summary", "academy-help-article-title");
    body.append(heading, element("p", content.support));
    const notice = element("section", undefined, "academy-help-context");
    notice.append(
      element("h4", "This summary is not sent"),
      element(
        "p",
        "Live support is not connected in this preview. Copy or download the summary and share it through your established Academy or institution contact. No ticket is created.",
      ),
    );
    body.append(notice);
    const label = element("label", "Describe the problem and what you tried", "academy-help-label");
    const input = element("textarea");
    input.rows = 7;
    input.value = supportDraft;
    input.placeholder =
      "What were you trying to do?\nWhat happened instead?\nWhat exact message appeared?\nWhat have you already tried?";
    input.addEventListener("input", () => {
      supportDraft = input.value;
    });
    input.setAttribute("aria-describedby", "academy-help-privacy");
    label.append(input);
    const privacy = element(
      "p",
      "Include only the details needed to investigate. Leave out passwords, sign-in codes, exam answers, payment details and private learner records. Your draft is kept only on this page and is cleared when you leave the page or reload.",
      "academy-help-note",
    );
    privacy.id = "academy-help-privacy";
    const outputLabel = element("label", "Summary to share", "academy-help-label");
    const output = element("textarea");
    output.rows = 9;
    output.readOnly = true;
    outputLabel.append(output);
    const status = element("p");
    status.setAttribute("role", "status");
    const prepare = () => {
      if (!input.value.trim()) {
        status.textContent = "Describe the issue before preparing your summary.";
        input.focus();
        return false;
      }
      output.value = `${content.title}\nPage: ${context.title}\nLocation: ${context.area}/${context.page}\n\n${input.value.trim()}\n\nPrepared locally; not submitted to support.`;
      return true;
    };
    const actions = element("div", undefined, "academy-help-actions");
    actions.append(
      button("Copy summary", () => {
        if (!prepare()) return;
        // Keep selectable text available when clipboard permissions are denied.
        output.focus();
        output.select();
        if (!navigator.clipboard?.writeText) {
          status.textContent = "Select and copy the summary below. Clipboard access is unavailable.";
          return;
        }
        void navigator.clipboard.writeText(output.value).then(
          () => {
            status.textContent = "Summary copied. It has not been sent.";
          },
          () => {
            status.textContent = "Select and copy the summary below. Clipboard access was denied.";
          },
        );
      }),
      button("Download summary", () => {
        if (!prepare()) return;
        const url = URL.createObjectURL(new Blob([output.value], { type: "text/plain;charset=utf-8" }));
        const link = element("a");
        link.href = url;
        link.download = `${content.app}-support-summary.txt`;
        body.append(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        status.textContent = "Summary downloaded. It has not been sent.";
      }),
    );
    body.append(label, privacy, actions, status, outputLabel);
    focusHeading(heading);
  }

  function open(guideId?: string) {
    if (!dialog.open) {
      returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = "hidden";
      dialog.showModal();
    }
    const guide = content.guides.find(item => item.id === guideId);
    if (guide) renderGuide(guide);
    else renderLibrary(true);
  }
  const handleOpen = (event: Event) => open((event as CustomEvent<string>).detail);
  dialog.addEventListener("cancel", event => {
    event.preventDefault();
    close();
  });
  dialog.addEventListener("keydown", event => {
    if (event.key === "Tab") {
      const controls = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, a[href], [tabindex="0"]',
        ),
      ).filter(control => control.getClientRects().length > 0);
      if (controls.length) {
        event.preventDefault();
        const active = document.activeElement;
        const index = controls.indexOf(active as HTMLElement);
        if (index >= 0) {
          controls[(index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length].focus();
        } else {
          // Article headings receive programmatic focus. Continue in reading order from them.
          const ordered = event.shiftKey ? [...controls].reverse() : controls;
          const direction = event.shiftKey
            ? Node.DOCUMENT_POSITION_PRECEDING
            : Node.DOCUMENT_POSITION_FOLLOWING;
          const next = ordered.find(control => active && active.compareDocumentPosition(control) & direction);
          (next || ordered[0]).focus();
        }
      }
    }
    // Do not also dismiss the workspace's menus or trigger its search shortcuts.
    event.stopPropagation();
  });
  dialog.addEventListener("click", event => {
    if (event.target === dialog) close();
  });
  window.addEventListener(helpEvent, handleOpen);
  return () => {
    window.removeEventListener(helpEvent, handleOpen);
    if (dialog.open) document.documentElement.style.overflow = previousOverflow;
    dialog.close();
    dialog.remove();
    launcher.remove();
  };
}
